import { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import {
   FaBullhorn,
   FaClock,
   FaEnvelope,
   FaFacebook,
   FaHandshake,
   FaInstagram,
   FaMapMarkerAlt,
   FaPhoneAlt,
   FaTwitter
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Footer from './Footer';
import Header from './Header';

const ContactUs = () => {
   const [formData, setFormData] = useState({
      name: '',
      email: '',
      subject: '',
      message: ''
   });

   const [captchaToken, setCaptchaToken] = useState(null);
   const recaptchaRef = useRef();

   const handleCaptchaChange = (token) => {
      setCaptchaToken(token);
   };

   const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({
         ...prev,
         [name]: value
      }));
   };

   const handleSubmit = async (e) => {
      e.preventDefault();

      if (!captchaToken) {
         toast.warn("Please verify you're not a robot.");
         return;
      }

      try {
         const response = await fetch('/auth/contact', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...formData, captchaToken })
         });

         const data = await response.json();
         if (response.ok) {
            toast.success('Message sent successfully!');
            setFormData({ name: '', email: '', subject: '', message: '' });
            setCaptchaToken(null);
            recaptchaRef.current.reset();
         } else {
            toast.error(data.message || 'Failed to send message.');
         }
      } catch (err) {
         console.error('Error:', err);
         toast.error('An error occurred. Please try again.');
      }
   };

   return (
      <div>
         <Header />

         <div className="bg-[#f9fdf4] px-4 md:px-6 py-10 text-[#173e2a]">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-10 text-[#276549]">Contact Us</h2>

            <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10">
               {/* Left Section */}
               <div className="space-y-8">
                  <p className="text-[#1a4632] text-lg font-semibold italic">
                     At FareFuel, we’re driving change—bottle by bottle, mile by mile. Whether you're redeeming your NY 5¢ bottle deposit credits,
                     recycling containers marked CTRV:ME NY, or exploring our eco-rewards program, we’re here to help.
                  </p>

                  <div>
                     <h3 className="font-bold text-xl mb-3 text-[#9cc026]">General Inquiries</h3>
                     <div className="space-y-4 text-gray-700">
                        <div className="flex items-start gap-3">
                           <FaEnvelope className="mt-1 text-[#9cc026] w-5 h-5 self-start" />
                           <p><strong>Email:</strong> support@farefuel.com</p>
                        </div>
                        <div className="flex items-start gap-3">
                           <FaPhoneAlt className="mt-1 text-[#9cc026] w-5 h-5 self-start" />
                           <p><strong>Phone:</strong> + (212) 555-3835)</p>
                        </div>
                        <div className="flex items-start gap-3">
                           <FaClock className="mt-1 text-[#9cc026] w-5 h-5 self-start" />
                           <p><strong>Hours:</strong> Monday – Friday, 9:00 AM – 6:00 PM (EST)</p>
                        </div>
                     </div>
                  </div>

                  <div>
                     <h3 className="font-bold text-xl mb-3 text-[#9cc026]">Mailing Address</h3>
                     <p className="flex items-start gap-2 text-gray-700">
                        <FaMapMarkerAlt className="text-[#9cc026] mt-1 w-5 h-5 self-start" />
                        <span>
                           FareFuel Corp<br />
                           123 Green Mile Avenue, Suite 500<br />
                           New York, NY 10001
                        </span>
                     </p>
                  </div>

                  <div>
                     <h3 className="font-bold text-xl mb-3 text-[#9cc026]">Corporate & Media</h3>
                     <div className="space-y-3 text-gray-700">
                        <p className="flex items-start gap-2">
                           <FaHandshake className="text-[#9cc026] mt-1 w-5 h-5 self-start" />
                           <span><strong>Partnerships:</strong> partnerships@farefuel.com</span>
                        </p>
                        <p className="flex items-start gap-2">
                           <FaBullhorn className="text-[#9cc026] mt-1 w-5 h-5 self-start" />
                           <span><strong>Media & PR:</strong> press@farefuel.com</span>
                        </p>
                     </div>
                  </div>
               </div>

               {/* Right Form */}
               <div className="bg-[#eef7dd] p-6 sm:p-8 rounded-md shadow-md">
                  <h3 className="text-[#9cc026] text-2xl sm:text-3xl font-bold mb-6">Send Us a <span className="text-[#1a4632]">Message</span></h3>
                  <form className="space-y-4" onSubmit={handleSubmit}>
                     <input
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Name"
                        required
                        className="w-full border-b border-gray-300 bg-transparent py-2 focus:outline-none focus:border-b-2 focus:border-green-500"
                     />
                     <input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Email"
                        required
                        className="w-full border-b border-gray-300 bg-transparent py-2 focus:outline-none focus:border-b-2 focus:border-green-500"
                     />
                     <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="Subject"
                        required
                        className="w-full border-b border-gray-300 bg-transparent py-2 focus:outline-none focus:border-b-2 focus:border-green-500"
                     />
                     <textarea
                        name="message"
                        placeholder="Message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="4"
                        required
                        className="w-full border-b border-gray-300 bg-transparent py-2 focus:outline-none focus:border-b-2 focus:border-green-500"
                     />

                     <ReCAPTCHA
                        sitekey="6LcYJYosAAAAALUh_iQ6L7frGguoQ9ZEzBuWvxNX"
                        onChange={handleCaptchaChange}
                        ref={recaptchaRef}
                     />

                     <button
                        type="submit"
                        className="bg-green-600 text-white font-semibold py-2 px-6 rounded-full hover:bg-[#6bb01f] transition-all"
                     >
                        Submit Message
                     </button>
                  </form>
               </div>
            </div>

            {/* Social + Help Section */}
            <div className="max-w-7xl mx-auto py-16">
               <div className="bg-[#173e2a] rounded-xl shadow-md p-6 sm:p-8 space-y-10 md:space-y-0 md:flex md:gap-8 md:justify-between">
                  {/* Social Section */}
                  <div className="md:w-1/2 bg-[#eef7dd] p-5 sm:p-6 rounded-lg">
                     <h3 className="text-2xl font-bold text-[#9cc026] mb-4 text-center md:text-left">Stay Connected</h3>
                     <div className="space-y-4 text-gray-700 text-lg">
                        <p className="flex items-center gap-3">
                           <FaInstagram className="text-pink-600 text-xl" />
                           <span><strong>@FareFuel</strong></span>
                        </p>
                        <p className="flex items-center gap-3">
                           <FaTwitter className="text-blue-500 text-xl" />
                           <span><strong>@FareFuelNYC</strong></span>
                        </p>
                        <p className="flex items-center gap-3">
                           <FaFacebook className="text-blue-700 text-xl" />
                           <span><strong>FareFuel Official</strong></span>
                        </p>
                     </div>
                  </div>

                  {/* Help Section */}
                  <div className="md:w-1/2 space-y-4 mt-10 md:mt-0">
                     <h3 className="text-2xl font-bold text-[#9cc026]">Need Redeeming Your Bottles?</h3>
                     <p className="text-gray-100 text-lg">
                        If your container is marked <strong>CTRV:ME NY</strong> or <strong>NY 5¢</strong>, just:
                     </p>
                     <ul className="list-disc list-inside text-gray-200 text-lg space-y-1">
                        <li>Drop off at a FareFuel location.</li>
                        <li>Collect your refund voucher.</li>
                        <li>Redeem for gas, EV charging, or FareFuel credits.</li>
                     </ul>
                     <p className="text-gray-300">Questions? Use the form above — we’re happy to help.</p>
                  </div>
               </div>
            </div>
         </div>

         <Footer />
         <ToastContainer position="top-right" autoClose={3000} />
      </div>
   );
};

export default ContactUs;
